/**
 * Equipment Return Service
 * บริการสำหรับจัดการระบบส่งคืนอุปกรณ์แบบรวมศูนย์
 */

import ApiWithLogging from '../utils/apiWithLogging';
import type {
  ReturnItem,
  ReturnItemListParams,
  ReturnItemUpdateData,
  ItemFollowUp,
  ItemFollowUpCreateData,
  BulkFollowUpData,
  DashboardStats,
  ManualReturnRequestData,
  ReturnRequest,
  RentalOptionForReturn
} from '../types/equipment-return';

const BASE_URL = '/equipment-returns';

/**
 * Equipment Return Service
 * บริการจัดการการส่งคืนอุปกรณ์ทั้งหมดในระบบ
 */
export const equipmentReturnService = {
  // ============================================
  // Return Items Management
  // ============================================

  /**
   * ดึงรายการส่งคืนทั้งหมดตามเงื่อนไข
   * @param params - พารามิเตอร์การค้นหาและกรอง
   * @returns Promise<{ items: ReturnItem[], total: number, page: number, limit: number }>
   */
  getItems: async (params: ReturnItemListParams) => {
    return await ApiWithLogging.get<{
      items: ReturnItem[];
      total: number;
      page: number;
      limit: number;
    }>(`${BASE_URL}/items`, { params });
  },

  /**
   * ดึงรายละเอียดรายการส่งคืน 1 รายการ
   * @param id - ID ของรายการส่งคืน
   * @returns Promise<ReturnItem>
   */
  getItem: async (id: number) => {
    return await ApiWithLogging.get<ReturnItem>(`${BASE_URL}/items/${id}`);
  },

  /**
   * อัพเดทข้อมูลรายการส่งคืน
   * @param id - ID ของรายการส่งคืน
   * @param data - ข้อมูลที่ต้องการอัพเดท
   * @returns Promise<ReturnItem>
   */
  updateItem: async (id: number, data: ReturnItemUpdateData) => {
    return await ApiWithLogging.put<ReturnItem>(
      `${BASE_URL}/items/${id}`,
      data,
      {},
      {
        service: 'EquipmentReturn',
        action: 'update_return_item',
        entityType: 'return_item',
        entityId: id,
      }
    );
  },

  /**
   * Alias สำหรับ updateItem (ใช้ใน ReturnItemDetailModal)
   */
  updateReturnItem: async (id: number, data: ReturnItemUpdateData) => {
    return equipmentReturnService.updateItem(id, data);
  },

  /**
   * ดึงรายการส่งคืนที่เกี่ยวข้อง (จาก Return Request เดียวกัน)
   * @param id - ID ของรายการส่งคืน
   * @returns Promise<ReturnItem[]>
   */
  getRelatedItems: async (id: number) => {
    return await ApiWithLogging.get<ReturnItem[]>(`${BASE_URL}/items/${id}/related`);
  },

  // ============================================
  // Follow-ups Management
  // ============================================

  /**
   * เพิ่มการติดตามผลการส่งคืน
   * @param itemId - ID ของรายการส่งคืน
   * @param data - ข้อมูลการติดตามผล
   * @returns Promise<ItemFollowUp>
   */
  addFollowUp: async (itemId: number, data: ItemFollowUpCreateData) => {
    return await ApiWithLogging.post<ItemFollowUp>(
      `${BASE_URL}/items/${itemId}/follow-ups`,
      data,
      {},
      {
        service: 'EquipmentReturn',
        action: 'add_follow_up',
        entityType: 'return_item',
        entityId: itemId,
      }
    );
  },

  /**
   * Alias สำหรับ addFollowUp (ใช้ใน ItemFollowUpModal)
   */
  createItemFollowUp: async (itemId: number, data: ItemFollowUpCreateData) => {
    return equipmentReturnService.addFollowUp(itemId, data);
  },

  /**
   * ดึงประวัติการติดตามผลทั้งหมดของรายการส่งคืน
   * @param itemId - ID ของรายการส่งคืน
   * @returns Promise<ItemFollowUp[]>
   */
  getFollowUps: async (itemId: number) => {
    return await ApiWithLogging.get<ItemFollowUp[]>(`${BASE_URL}/items/${itemId}/follow-ups`);
  },

  /**
   * Alias สำหรับ getFollowUps (ใช้ใน ReturnItemDetailModal)
   */
  getItemFollowUps: async (itemId: number) => {
    return equipmentReturnService.getFollowUps(itemId);
  },

  /**
   * ติดตามผลหลายรายการพร้อมกัน (Bulk Follow-up)
   * @param data - ข้อมูล item IDs และข้อมูลการติดตามผล
   * @returns Promise<ItemFollowUp[]>
   */
  bulkFollowUp: async (data: BulkFollowUpData) => {
    return await ApiWithLogging.post<ItemFollowUp[]>(
      `${BASE_URL}/items/bulk-follow-up`,
      data,
      {},
      {
        service: 'EquipmentReturn',
        action: 'bulk_follow_up',
        entityType: 'return_item',
      }
    );
  },

  /**
   * Alias สำหรับ bulkFollowUp (ใช้ใน ItemFollowUpModal)
   */
  bulkCreateFollowUp: async (data: BulkFollowUpData) => {
    return equipmentReturnService.bulkFollowUp(data);
  },

  // ============================================
  // Dashboard & Statistics
  // ============================================

  /**
   * ดึงสถิติสำหรับ Dashboard
   * @returns Promise<DashboardStats>
   */
  getDashboardStats: async () => {
    return await ApiWithLogging.get<DashboardStats>(`${BASE_URL}/dashboard/stats`);
  },

  // ============================================
  // Manual Return Request
  // ============================================

  /**
   * สร้างคำขอส่งคืนแบบ Manual
   * @param data - ข้อมูลคำขอส่งคืนและรายการอุปกรณ์
   * @returns Promise<ReturnRequest>
   */
  createManualRequest: async (data: ManualReturnRequestData) => {
    return await ApiWithLogging.post<ReturnRequest>(
      `${BASE_URL}/requests`,
      data,
      {},
      {
        service: 'EquipmentReturn',
        action: 'create_manual_return_request',
        entityType: 'return_request',
      }
    );
  },

  /**
   * Alias สำหรับ createManualRequest (ใช้ใน ManualReturnRequestModal)
   */
  createManualReturnRequest: async (data: ManualReturnRequestData) => {
    return equipmentReturnService.createManualRequest(data);
  },

  /**
   * ดึงรายการเช่าที่สามารถสร้างคำขอส่งคืนได้
   * (เช่าที่ยังมีอุปกรณ์อยู่และไม่มี pending return request)
   * @returns Promise<RentalOptionForReturn[]>
   */
  getAvailableRentals: async (params?: { search?: string; page?: number; limit?: number }) => {
    return await ApiWithLogging.get<{ items: RentalOptionForReturn[]; total: number; page: number; limit: number; total_pages: number }>(`${BASE_URL}/rentals/available`, { params });
  },

  /**
   * สร้าง Return Request จากการสิ้นสุดสัญญา (CONTRACT_END)
   * @param data - ข้อมูลการสร้าง Return Request จากการหมดสัญญา
   * @returns Promise<ReturnRequest>
   */
  createFromContractEnd: async (data: {
    rental_id: number;
    scheduled_date?: string;
    notes?: string;
  }) => {
    return await ApiWithLogging.post<ReturnRequest>(
      `${BASE_URL}/from-contract-end`,
      data,
      {},
      {
        service: 'EquipmentReturn',
        action: 'create_from_contract_end',
        entityType: 'return_request',
      }
    );
  },
  // หมายเหตุ (B12/MAIN-4, 8 ก.ย. 2026): `getReturnRequest`/`getReturnRequests` ถูกลบ —
  // backend ไม่มี GET /equipment-returns/requests(/{id}) (มีแค่ POST) และไม่มีหน้าจอเรียก
};

export default equipmentReturnService;
